import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useHasMounted from 'hooks/useHasMounted'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { getInteractionStudioDataFromOfferPage } from 'selectors/interactionStudioSelectors'
import { OfferPackageOptionView, canBookNow, getCalendarMonths, getDaysOtherAvailability, getPackagesForFilters, getSelectedOfferPageDay, getSelectedPackageView, isFetchingCalendars } from 'selectors/offerPage/offerPageSelectors'
import OfferCalendarDayPicker from './DayPicker/OfferCalendarDayPicker'
import OfferCalendarMonthPicker from './MonthPicker/OfferCalendarMonthPicker'
import OfferCalendarNoFlightsMessage from './OfferCalendarNoFlightsMessage'
import OfferCalendarNotAvailableMessage from './OfferCalendarNotAvailableMessage'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownSheetBody from 'LuxKit/Dropdown/Sheet/DropdownSheetBody'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import ModalProvider from 'contexts/ModalProvider'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isMultiCartEnabled, shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import {
  getMultiRoomCalendarMonths,
  getMultiRoomDaysOtherAvailability,
  getMultiRoomPackagesForFilters,
  getSelectedOfferPageMultiRoomDay,
  isAnyMultiRoomFlightsActive,
  isMultiRoomFetchingCalendars,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import OfferCalendarDatePickerDropdownHeader from './OfferCalendarDatePickerDropdownHeader'
import OfferCalendarDatePickerDropdownFooter from './OfferCalendarDatePickerDropdownFooter'
import CalendarPickerRecommendations from './Recommendations/CalendarPickerRecommendations'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OfferCalendarflightDealBanner from './OfferCalendarFlightDealBanner'
import useLoyaltyOfferPageTargetRoomUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetRoomUpgrade'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'

interface Props {
  offer: App.Offer;
  airport: App.Airport;
  checkIn?: string;
  fetching?: boolean;
  selectedDay?: App.CalendarDay;
  flights?: boolean;
  calendarError?: boolean;
  calendarAvailability: boolean;
  useRooms?: boolean;
  defaultDayPicker?: boolean;
  defaultMonth?: string;
  duration: number;
  relevantPackages: Array<OfferPackageOptionView>;
  selectedPackageView?: OfferPackageOptionView;
  recommendationsShown: boolean;
  rooms?: Array<App.Occupants>;
  otherAvailability: { [checkIn: string]: boolean };
  selectedPackage: App.Package,
  interactionStudioData?: App.ISACheckoutData;
  isAnyFlightsActive: boolean;
  subFilters?: Array<OfferPageFilterType>;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  canBookNow?: boolean;
  isLuxPlusEnabled: boolean;
  userMultiItemCartEnabled: boolean;
}

const DEFAULT_SUB_FILTERS: Array<OfferPageFilterType> = ['duration', 'airport', 'room']

function OfferCalendarDatePicker(props: Props) {
  const {
    checkIn,
    offer,
    fetching,
    selectedDay,
    flights,
    calendarError,
    calendarAvailability,
    defaultMonth,
    defaultDayPicker,
    duration,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    selectedPackage,
    rooms = EmptyArray,
    recommendationsShown,
    interactionStudioData,
    isAnyFlightsActive,
    subFilters = DEFAULT_SUB_FILTERS,
    scrollRef,
    canBookNow,
    isLuxPlusEnabled,
    airport,
    userMultiItemCartEnabled,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const [monthIndex, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const mounted = useHasMounted()

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetRoomUpgrade(selectedPackage)
  const canAddToUserCart = userMultiItemCartEnabled && !selectedTargetUpgradePackage

  useEffect(() => {
    if (!fetching && checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [checkIn, selectedDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onBackClick = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }

    setMonth(undefined)
  }, [setMonth, dispatch, recommendationsShown])

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }
    dispatch({ type: OfferPageActions.resetDates })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [dispatch, onUserAnalyticsAction, recommendationsShown])

  useEffect(() => {
    if (offer.luxPlus.hasMemberPrices && isLuxPlusEnabled) {
      dispatch({ type: OfferPageActions.setDisplayMemberPrice, displayMemberPrice: true })
    }
  }, [offer, dispatch, isLuxPlusEnabled])

  // calendar reco on mobile is handled separately by OfferCalendarPickerRecommendationsModal
  const isMobile = useScreenSizeOnly('mobile')

  return (<ModalProvider>
    <OfferCalendarDatePickerDropdownHeader
      subFilters={subFilters}
      monthIndex={monthIndex}
      onBackClick={onBackClick}
      onDismissClick={onDropdownClose}
    />
    <DropdownSheetBody ref={scrollRef}>
      {recommendationsShown && !isMobile && (<CalendarPickerRecommendations
        offerId={offer.id}
        offerType={offer.type}
        rooms={rooms}
        heading={`Similar hotels to ${offer.property?.name ?? offer.location}`}
        seedOffer={offer}
      />)}
      {!recommendationsShown && (
        <>
          {mounted && !fetching && !calendarAvailability && <>
            {(calendarError || !flights) && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
            {!calendarError && flights && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
          </>}
          {calendarAvailability && <VerticalSpacer gap={24}>
            {isAnyFlightsActive && <Caption variant="medium" colour="neutral-two">
              *Price is indicative and based on sample return fares{' '}
              (inclusive of booking fees on Low Cost Carriers). To view live pricing,{' '}
              conduct a full search. {config.brandConfig.name} full Terms and Conditions apply
            </Caption>}
            {isAnyFlightsActive && <OfferCalendarflightDealBanner
              destinationAirportCode={offer.flightDestinationPort}
              originAirportCode={airport.code}
            />}
            {monthIndex !== undefined && !fetching && <OfferCalendarDayPicker
              onBackToMonth={onBackToMonth}
              initialMonth={monthIndex}
              defaultDayPicker={defaultDayPicker}
              flightsOnly={offer.bundledWithFlightsOnly}
              contentRef={scrollRef}
            />}
            {(monthIndex === undefined || fetching) && <OfferCalendarMonthPicker
              fetching={fetching}
              onMonthSelect={setMonth}
            />}
          </VerticalSpacer>}
        </>
      )}
    </DropdownSheetBody>
    <OfferCalendarDatePickerDropdownFooter
      userMultiItemCartEnabled={canAddToUserCart}
      canBook={canBookNow}
      offer={offer}
      selectedDay={selectedDay}
      duration={duration}
      relevantPackages={relevantPackages}
      selectedPackageView={selectedPackageView}
      interactionStudioData={interactionStudioData}
      isAnyFlightsActive={isAnyFlightsActive}
    />
  </ModalProvider>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    checkIn: state.checkInDate,
    offer: state.offer,
    airport: state.airport,
    flights: state.flights,
    duration: state.duration,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(state.offer),
    relevantPackages: isMultiRoom ? getMultiRoomPackagesForFilters(state, appState) : getPackagesForFilters(appState, state),
    selectedPackageView: getSelectedPackageView(state, appState),
    rooms: state.rooms,
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    fetching: isMultiRoom ? isMultiRoomFetchingCalendars(state, appState) : isFetchingCalendars(appState, state),
    calendarAvailability: isMultiRoom ? getMultiRoomCalendarMonths(state, appState).availableCount > 0 : getCalendarMonths(appState, state).availableCount > 0,
    calendarError: !!state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    selectedPackage: state.package,
    interactionStudioData: getInteractionStudioDataFromOfferPage(appState, state),
    isAnyFlightsActive: isMultiRoom ? isAnyMultiRoomFlightsActive(state, appState) : state.flights,
    canBookNow: canBookNow(state, appState),
    isLuxPlusEnabled: getIsLuxPlusEnabled(appState),
    userMultiItemCartEnabled: isMultiCartEnabled(appState),
    open: !!state.activeFilter,
  }
}, OfferCalendarDatePicker, preventRenderWithClosedFilter)
